import { mergeAttributes, Node } from "@tiptap/react";
import { ReactNodeViewRenderer } from "@tiptap/react";

import { PdfUploadNode as PdfUploadNodeComponent } from "./pdf-upload-node";

export type UploadFunction = (
  file: File,
  onProgress?: (event: { progress: number }) => void,
  abortSignal?: AbortSignal,
) => Promise<string>;

export interface PdfUploadNodeOptions {
  /**
   * Acceptable file types for upload.
   * @default 'application/pdf'
   */
  accept?: string;
  /**
   * Maximum number of files that can be uploaded.
   * @default 1
   */
  limit?: number;
  /**
   * Maximum file size in bytes (0 for unlimited).
   * @default 0
   */
  maxSize?: number;
  /**
   * Function to handle the upload process.
   */
  upload?: UploadFunction;
  /**
   * Callback for upload errors.
   */
  onError?: (error: Error) => void;
  /**
   * Callback for successful uploads.
   */
  onSuccess?: (url: string) => void;
}

declare module "@tiptap/react" {
  interface Commands<ReturnType> {
    pdfUpload: {
      setPdfUploadNode: (options?: PdfUploadNodeOptions) => ReturnType;
    };
  }
}

/**
 * A TipTap node extension that creates a PDF upload component.
 * @see registry/tiptap-node/pdf-upload-node/pdf-upload-node
 */
export const PdfUploadNode = Node.create<PdfUploadNodeOptions>({
  name: "pdfUpload",

  group: "block",

  draggable: true,

  selectable: true,

  atom: true,

  addOptions() {
    return {
      accept: "application/pdf",
      limit: 1,
      maxSize: 0,
      upload: undefined,
      onError: undefined,
      onSuccess: undefined,
    };
  },

  addAttributes() {
    return {
      accept: {
        default: this.options.accept,
      },
      limit: {
        default: this.options.limit,
      },
      maxSize: {
        default: this.options.maxSize,
      },
      file: {
        default: null,
      },
      url: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="pdf-upload"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes({ "data-type": "pdf-upload" }, HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(PdfUploadNodeComponent);
  },

  addCommands() {
    return {
      setPdfUploadNode:
        (options = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          });
        },
    };
  },

  /**
   * Adds Enter key handler to trigger the upload component when it's selected.
   */
  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => {
        const { selection } = editor.state;
        const { nodeAfter } = selection.$from;

        if (nodeAfter && nodeAfter.type.name === "pdfUpload" && editor.isActive("pdfUpload")) {
          const nodeEl = editor.view.nodeDOM(selection.$from.pos);
          if (nodeEl && nodeEl instanceof HTMLElement) {
            // Since NodeViewWrapper is wrapped with a div, we need to click the first child
            const firstChild = nodeEl.firstChild;
            if (firstChild && firstChild instanceof HTMLElement) {
              firstChild.click();
              return true;
            }
          }
        }
        return false;
      },
    };
  },
});

export default PdfUploadNode;
